import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Menu, Search, SquarePen } from 'lucide-react';
import { ApiError, UNAUTHORIZED_EVENT, api, type ThreadCategory, type ThreadViewName } from './lib/api';
import { useUiStore, type ComposerMode } from './lib/store';
import { useKilnEvents } from './lib/ws';
import { setCommandRegistry, useKeyboard } from './hooks/useKeyboard';
import {
  PALETTE_COMMANDS,
  SHELL_COMMANDS,
  useAppCommandContext,
} from './hooks/useAppCommands';
import type { GoTarget } from './lib/keymap';
import Login from './components/Login';
import { AppRail, NavDrawer } from './components/Sidebar';
import AccountsSettings from './components/AccountsSettings';
import UpdateToast from './components/UpdateToast';
import { ToastProvider } from './components/Toast';
import ThreadList from './components/ThreadList';
import SplitTabs from './components/SplitTabs';
import AppearanceSettings from './components/AppearanceSettings';
import ReadingPane, { type ReplyMode } from './components/ReadingPane';
import CommandPalette, { type PaletteMode } from './components/CommandPalette';
import Composer, { type ComposerIntent } from './components/Composer';
import { SearchBar, type SearchBarHandle, type SearchState } from './components/SearchBar';
import styles from './App.module.css';

/**
 * The shell is two elements wide and no more:
 *
 *   [ 40px icon rail ][ one main column ]
 *
 * There is no folder column and no list-beside-message split, because neither
 * exists in the thing this is built to be. The rail is always visible in every
 * state; folders live in an overlay drawer that is opened, used and dismissed;
 * and opening a conversation REPLACES the list in the main column rather than
 * shrinking it.
 *
 * The list is not unmounted while reading — it is hidden with `visibility`, so
 * its scroll offset, its cursor row and its pagination all survive intact, and
 * Esc puts the user back on the exact row they left. It is also why j/k keep
 * working while the reading view is up: the list still owns the cursor, it just
 * loads each landing into the pane instead of only moving the highlight.
 */

// The dispatch table is a module-level registry; trim it once, at load, to the
// commands this shell can actually honour.
setCommandRegistry(SHELL_COMMANDS);

const IMAGE_DOMAINS_KEY = 'kiln.allowedImageDomains';
const LAST_ACCOUNT_KEY = 'kiln.lastAccountUsed';

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* the session still works; the preference just will not survive a reload */
  }
}

// --- Input modality ----------------------------------------------------------

/**
 * Stamps the last input modality on <html>. global.css keys the "no transition
 * on a keypress" rule off it: while the user is driving with j/k/e/#/r every
 * state change lands in 0ms, and the pointer restores the normal easing.
 */
function useInputModality(): void {
  useEffect(() => {
    const set = (mode: 'keyboard' | 'pointer') => {
      document.documentElement.dataset.input = mode;
    };
    const onKeyDown = () => set('keyboard');
    const onPointerDown = () => set('pointer');
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, []);
}

// --- Shell -------------------------------------------------------------------

const GO_TARGETS: Record<
  Exclude<GoTarget, 'drafts'>,
  { view: ThreadViewName; category: ThreadCategory | null }
> = {
  inbox: { view: 'inbox', category: null },
  all: { view: 'all', category: null },
  starred: { view: 'starred', category: null },
  sent: { view: 'sent', category: null },
  done: { view: 'archive', category: null },
  snoozed: { view: 'snoozed', category: null },
  unread: { view: 'unread', category: null },
  newsletters: { view: 'inbox', category: 'newsletters' },
  receipts: { view: 'inbox', category: 'receipts' },
};

const REPLY_MODE: Record<ReplyMode, ComposerMode> = {
  reply: 'reply',
  replyAll: 'reply-all',
  forward: 'forward',
};

/** True when a keystroke belongs to a text field or a modal, not to the shell. */
function typingInto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return true;
  return target.closest('[role="dialog"]') !== null;
}

function AuthenticatedShell({ aiEnabled }: { aiEnabled: boolean }) {
  useKilnEvents();
  useInputModality();

  const queryClient = useQueryClient();

  const setView = useUiStore((s) => s.setView);
  const category = useUiStore((s) => s.category);
  const setCategory = useUiStore((s) => s.setCategory);
  const accountFilter = useUiStore((s) => s.accountFilter);
  const setAccountFilter = useUiStore((s) => s.setAccountFilter);
  const selectedThreadKey = useUiStore((s) => s.selectedThreadKey);
  const setSelectedThreadKey = useUiStore((s) => s.setSelectedThreadKey);
  const focusMode = useUiStore((s) => s.focusMode);
  const toggleFocusMode = useUiStore((s) => s.toggleFocusMode);
  const drawerOpen = useUiStore((s) => s.drawerOpen);
  const drawerPinned = useUiStore((s) => s.drawerPinned);
  const setDrawerPinned = useUiStore((s) => s.setDrawerPinned);
  const setDrawerOpen = useUiStore((s) => s.setDrawerOpen);
  const paletteOpen = useUiStore((s) => s.paletteOpen);
  const setPaletteOpen = useUiStore((s) => s.setPaletteOpen);
  const settingsOpen = useUiStore((s) => s.settingsOpen);
  const setSettingsOpen = useUiStore((s) => s.setSettingsOpen);
  const appearanceOpen = useUiStore((s) => s.appearanceOpen);
  const readingLayout = useUiStore((s) => s.appearance.readingLayout);
  const setAppearanceOpen = useUiStore((s) => s.setAppearanceOpen);
  const composer = useUiStore((s) => s.composer);
  const openComposer = useUiStore((s) => s.openComposer);
  const closeComposer = useUiStore((s) => s.closeComposer);

  const [paletteMode, setPaletteMode] = useState<PaletteMode>('palette');
  const [search, setSearch] = useState<SearchState | null>(null);
  const [searchActive, setSearchActive] = useState(false);
  const [allowedImageDomains, setAllowedImageDomains] = useState<string[]>(() => {
    const raw = readStored(IMAGE_DOMAINS_KEY);
    if (!raw) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((d): d is string => typeof d === 'string') : [];
    } catch {
      return [];
    }
  });

  const searchBarRef = useRef<SearchBarHandle>(null);
  const listScrollRef = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const readingRef = useRef<HTMLElement>(null);

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts() });
  const accounts = useMemo(() => accountsQuery.data?.accounts ?? [], [accountsQuery.data]);

  // The composer needs the open thread for reply headers and the collapsed
  // reply bar. The reading view has already fetched it, so this is a cache read.
  const threadQuery = useQuery({
    queryKey: ['thread', selectedThreadKey],
    queryFn: () => {
      if (selectedThreadKey === null) throw new Error('no thread selected');
      return api.thread(selectedThreadKey);
    },
    enabled: selectedThreadKey !== null,
  });

  const searchQuery = search?.active && search.query ? search.query : null;
  const reading = selectedThreadKey !== null;

  /**
   * Search mode lives in the tab row, and the tab row is part of the list — so
   * entering it has to come back out of the reading view, or the field would
   * open behind the conversation. Handled here rather than in `focusSearch`
   * because the command palette reaches the field directly.
   */
  const onSearchActiveChange = useCallback(
    (next: boolean) => {
      setSearchActive(next);
      if (next) setSelectedThreadKey(null);
    },
    [setSelectedThreadKey],
  );

  /* --- launch URLs ------------------------------------------------------- */

  useEffect(() => {
    const { pathname, search: qs } = window.location;
    if (pathname !== '/compose' && pathname !== '/search') return;

    if (pathname === '/search') {
      setPaletteMode('palette');
      setPaletteOpen(true);
    } else {
      const params = new URLSearchParams(qs);
      // A protocol handler delivers the whole mailto: URL in `to`, headers and
      // all; a plain shortcut delivers a bare address or nothing.
      const [addresses, headerQuery] = (params.get('to') ?? '')
        .replace(/^mailto:/i, '')
        .split('?', 2);
      const headers = new URLSearchParams(headerQuery ?? '');
      const parse = (value: string) =>
        value
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean)
          .map((address) => ({ address }));

      openComposer({
        mode: 'new',
        to: parse(decodeURIComponent(addresses ?? '')),
        cc: parse(headers.get('cc') ?? ''),
        subject: headers.get('subject') ?? '',
        bodyHtml: headers.get('body') ?? '',
      });
    }

    window.history.replaceState(null, '', '/');
  }, [openComposer, setPaletteOpen]);

  /**
   * Focus follows the layer. Without this, opening a conversation would leave
   * focus on a row inside a layer that has just become invisible, and closing
   * it would leave focus nowhere — which is also what makes ⇥ stop cycling the
   * splits, since that binding is only borrowed while focus sits in the list.
   */
  useEffect(() => {
    if (reading) readingRef.current?.focus({ preventScroll: true });
    else listRef.current?.focus({ preventScroll: true });
  }, [reading]);

  /* --- the drawer -------------------------------------------------------- */

  /**
   * The hamburger and Cmd+\ mean "show or hide navigation", which has to work
   * whichever form it is currently in. Pinned, hiding means unpinning *and*
   * closing: leaving the overlay behind would make one press look like nothing
   * happened. The pin button is the only thing that pins.
   */
  const toggleDrawer = useCallback(() => {
    if (drawerPinned) {
      setDrawerPinned(false);
      setDrawerOpen(false);
      return;
    }
    setDrawerOpen(!drawerOpen);
  }, [drawerPinned, setDrawerPinned, drawerOpen, setDrawerOpen]);

  /**
   * Left Arrow and Cmd+\ both reach the drawer. Neither is in lib/keymap: the
   * keymap is a shared contract this shell does not own, and both of these are
   * facts about *this* layout — Left Arrow only means "reveal the thing to my
   * left" because there is a drawer to the left to reveal.
   */
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.defaultPrevented || typingInto(e.target)) return;

      if (e.key === '\\' && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        toggleDrawer();
        return;
      }

      if (e.key === 'ArrowLeft' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        // A pinned drawer is already revealed, and an arrow key should never be
        // able to tear down a layout the user deliberately fixed in place.
        if (drawerPinned) return;
        // Only from the list. In the reading view the arrow keys belong to the
        // message, and stealing one to open navigation would be a surprise.
        if (!drawerOpen && (reading || paletteOpen || settingsOpen || appearanceOpen)) return;
        e.preventDefault();
        setDrawerOpen(!drawerOpen);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    drawerOpen,
    drawerPinned,
    setDrawerOpen,
    toggleDrawer,
    reading,
    paletteOpen,
    settingsOpen,
    appearanceOpen,
  ]);

  /* --- command context --------------------------------------------------- */

  const goTo = useCallback(
    (target: GoTarget) => {
      const next = GO_TARGETS[target];
      setCategory(next.category);
      setView(next.view);
    },
    [setCategory, setView],
  );

  const compose = useCallback(
    (intent: ComposerIntent) => {
      openComposer({
        mode: intent.mode,
        popout: intent.popout === true,
        accountId: intent.accountId ?? null,
        to: intent.to ?? [],
        cc: intent.cc ?? [],
        bcc: intent.bcc ?? [],
        subject: intent.subject ?? '',
        bodyHtml: intent.bodyHtml ?? '',
        threadKey: intent.threadKey ?? null,
      });
    },
    [openComposer],
  );

  const openPalette = useCallback(
    (nextMode: PaletteMode) => {
      setPaletteMode(nextMode);
      setPaletteOpen(true);
    },
    [setPaletteOpen],
  );

  const focusSearch = useCallback(
    (allAccounts?: boolean) => {
      if (allAccounts) setAccountFilter(null);
      searchBarRef.current?.focus(allAccounts);
    },
    [setAccountFilter],
  );

  // Escape unwinds one layer at a time, innermost first.
  const goBack = useCallback(() => {
    if (composer.open) closeComposer();
    else if (paletteOpen) setPaletteOpen(false);
    else if (appearanceOpen) setAppearanceOpen(false);
    else if (settingsOpen) setSettingsOpen(false);
    else if (drawerOpen) setDrawerOpen(false);
    // The reading view is inside the search results, not beside them: Esc puts
    // the results back, and only a second Esc leaves search altogether.
    else if (selectedThreadKey) setSelectedThreadKey(null);
    else if (searchActive) searchBarRef.current?.close();
  }, [
    composer.open,
    closeComposer,
    paletteOpen,
    setPaletteOpen,
    appearanceOpen,
    setAppearanceOpen,
    settingsOpen,
    setSettingsOpen,
    drawerOpen,
    setDrawerOpen,
    searchActive,
    selectedThreadKey,
    setSelectedThreadKey,
  ]);

  /** Two surfaces can hold focus now, not three. */
  const cycleSplit = useCallback((direction: 1 | -1) => {
    const panes = [listRef.current, readingRef.current].filter(
      (el): el is HTMLElement => el !== null,
    );
    if (panes.length === 0) return;
    const current = panes.findIndex((el) => el.contains(document.activeElement));
    const next = panes[(current + direction + panes.length * 2) % panes.length];
    next?.focus();
  }, []);

  const ctx = useAppCommandContext({
    accounts,
    accountFilter,
    aiEnabled,
    selectedThreadKey,
    setAccountFilter,
    goTo,
    compose,
    openPalette,
    focusSearch,
    goBack,
    // There is no sidebar to toggle any more; the drawer is what this means.
    toggleSidebar: toggleDrawer,
    toggleFocusMode,
    cycleSplit,
  });

  useKeyboard('global', ctx);

  /* --- composer bridge ---------------------------------------------------- */

  const intent = useMemo<ComposerIntent | null>(
    () =>
      composer.open
        ? {
            mode: composer.mode,
            popout: composer.popout,
            threadKey: composer.threadKey,
            accountId: composer.accountId,
            to: composer.to,
            cc: composer.cc,
            bcc: composer.bcc,
            subject: composer.subject,
            bodyHtml: composer.bodyHtml,
          }
        : null,
    [composer],
  );

  const allowImageDomain = useCallback((domain: string) => {
    setAllowedImageDomains((prev) => {
      if (prev.includes(domain)) return prev;
      const next = [...prev, domain];
      writeStored(IMAGE_DOMAINS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <div className={styles.shell}>
      {!focusMode && <AppRail />}

      {drawerPinned && !focusMode && (
        <div className={styles.drawerColumn}>
          <NavDrawer />
        </div>
      )}

      <main className={styles.main}>
        <div className={styles.stage} data-layout={readingLayout}>
          {/* Hidden, never unmounted: this is what preserves the scroll offset,
              the cursor row and the loaded pages across a conversation.
              In a split layout it is never hidden at all — that is the point. */}
          <div
            className={styles.listLayer}
            data-hidden={reading && readingLayout === 'full'}
            data-pane="list"
            aria-hidden={reading && readingLayout === 'full'}
            ref={listRef}
            tabIndex={-1}
          >
            <div className={styles.topbar}>
              <button
                type="button"
                className={styles.iconButton}
                onClick={toggleDrawer}
                title="Mailboxes · ← or ⌘\"
                aria-label="Open mailboxes"
                aria-expanded={drawerOpen}
              >
                <Menu size={16} className="lucide" />
              </button>
              {!searchActive && <SplitTabs />}
              {/* Superhuman puts compose and search at the right end of the tab
                  row. Search was reachable only by pressing "/" — which is
                  perfect once you know it and undiscoverable until then. The
                  spacer pushes these to the trailing edge; both collapse while
                  the search field is open, since it takes the whole row. */}
              {!searchActive && (
                <>
                  <span className={styles.topbarSpacer} />
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => openComposer({ mode: 'new' })}
                    title="Compose · c"
                    aria-label="Compose"
                  >
                    <SquarePen size={16} className="lucide" />
                  </button>
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => searchBarRef.current?.focus()}
                    title="Search · /"
                    aria-label="Search mail"
                  >
                    <Search size={16} className="lucide" />
                  </button>
                </>
              )}
              <SearchBar
                ref={searchBarRef}
                aiEnabled={aiEnabled}
                accounts={accounts}
                accountFilter={accountFilter}
                scrollRef={listScrollRef}
                onResults={setSearch}
                onActiveChange={onSearchActiveChange}
              />
            </div>
            <div className={styles.paneBody}>
              <ThreadList
                category={category}
                searchQuery={searchQuery}
                scrollElementRef={listScrollRef}
                openOnMove={reading}
                keyboardEnabled={
                  !settingsOpen && !appearanceOpen && !composer.open && !drawerOpen
                }
              />
            </div>
          </div>

          {/* In a split layout the pane is always mounted, showing its own
              empty state, so selecting a conversation does not make the
              geometry jump. In `full` it exists only while reading. */}
          {(reading || readingLayout !== 'full') && (
            <section
              className={styles.readingLayer}
              aria-label="Message"
              ref={readingRef}
              tabIndex={-1}
            >
              <div className={styles.topbar}>
                {/* Nothing to go "back" to when the list never left. */}
                {readingLayout === 'full' && (
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => setSelectedThreadKey(null)}
                    title="Back to the list · Esc"
                    aria-label="Back to the list"
                  >
                    <ArrowLeft size={16} className="lucide" />
                  </button>
                )}
                <span className={styles.backHint}>
                  <kbd className="kiln-kbd">Esc</kbd>
                  <span>back</span>
                  <kbd className="kiln-kbd">j</kbd>
                  <kbd className="kiln-kbd">k</kbd>
                  <span>next</span>
                </span>
              </div>
              <div className={styles.paneBody}>
                <ReadingPane
                  aiEnabled={aiEnabled}
                  allowedImageDomains={allowedImageDomains}
                  onAllowImageDomain={allowImageDomain}
                  onReply={(message, replyMode) =>
                    compose({
                      mode: REPLY_MODE[replyMode],
                      threadKey: selectedThreadKey,
                      parentId: message.id,
                    })
                  }
                  onArchive={(key) => void api.threadAction(key, 'archive')}
                  onSnooze={() => ctx.snooze()}
                />
              </div>
            </section>
          )}
        </div>

        <Composer
          accounts={accounts}
          aiEnabled={aiEnabled}
          intent={intent}
          thread={threadQuery.data?.thread ?? null}
          messages={threadQuery.data?.messages ?? []}
          lastUsedAccountId={Number(readStored(LAST_ACCOUNT_KEY)) || null}
          onOpen={compose}
          onClose={closeComposer}
          onAccountUsed={(id) => writeStored(LAST_ACCOUNT_KEY, String(id))}
          onSent={() => {
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
            void queryClient.invalidateQueries({ queryKey: ['stats'] });
          }}
        />

        {/* The drawer belongs to the main column, not to the window: it slides
            over the list and leaves the rail alone, which is what keeps it
            reading as a disclosure rather than as a second app. */}
        {/* Only the transient form lives inside <main>; the pinned form is a
            sibling column rendered before it, so it displaces the content
            instead of covering it. */}
        {drawerOpen && !drawerPinned && (
          <>
            <div
              className={styles.drawerScrim}
              data-motion="overlay"
              onClick={() => setDrawerOpen(false)}
            />
            <div className={styles.drawerPanel} data-motion="overlay">
              <NavDrawer onNavigate={() => setDrawerOpen(false)} />
            </div>
          </>
        )}
      </main>

      <AccountsSettings />
      <AppearanceSettings />
      <CommandPalette
        open={paletteOpen}
        mode={paletteMode}
        ctx={ctx}
        commands={PALETTE_COMMANDS}
        onClose={() => setPaletteOpen(false)}
        onRunSearch={(query) => {
          setPaletteOpen(false);
          searchBarRef.current?.search(query);
        }}
      />
      <UpdateToast />
    </div>
  );
}

// --- Boot --------------------------------------------------------------------

function Boot({ children }: { children?: ReactNode }) {
  return (
    <div className={styles.boot}>
      <div className={styles.bootInner}>
        {children ?? <Loader2 size={20} className={`lucide ${styles.spinner}`} />}
      </div>
    </div>
  );
}

export default function App() {
  const queryClient = useQueryClient();
  const theme = useUiStore((s) => s.theme);
  const [signedOut, setSignedOut] = useState(false);

  // Any 401, from any call or from the event socket, drops the whole app.
  useEffect(() => {
    const onUnauthorized = () => setSignedOut(true);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  // Keeps <html data-theme> in step with the store after a manual change; the
  // first paint is already correct thanks to the inline script in index.html.
  // theme-color has to follow too, or the PWA title bar keeps the old shade.
  useEffect(() => {
    if (theme === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const paint = () => {
      const dark = theme === 'dark' || (theme === 'system' && media.matches);
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', dark ? '#08080A' : '#F1F0ED');
    };
    paint();
    media.addEventListener('change', paint);
    return () => media.removeEventListener('change', paint);
  }, [theme]);

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const unauthorized =
    signedOut || (me.isError && me.error instanceof ApiError && me.error.status === 401);

  if (unauthorized) {
    return (
      <Login
        onSuccess={() => {
          setSignedOut(false);
          void queryClient.invalidateQueries({ queryKey: ['me'] });
        }}
      />
    );
  }

  if (me.isPending) return <Boot />;

  if (me.isError) {
    return (
      <Boot>
        <span>Cannot reach the Kiln server.</span>
        <button type="button" className="kiln-button" onClick={() => void me.refetch()}>
          Retry
        </button>
      </Boot>
    );
  }

  return (
    <ToastProvider>
      <AuthenticatedShell aiEnabled={me.data.aiEnabled} />
    </ToastProvider>
  );
}
